import type { JSX } from "solid-js";
import { splitProps, createMemo } from "solid-js";
import { cn } from "@/utilities/cn";

const buttonVariants = {
  base: "active:animate-buttonClick inline-flex items-center justify-center active:animate-click rounded-md text-sm font-medium ring-offset-background transition-colors disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
  variant: {
    default: "bg-gray-600 text-white hover:bg-gray-500 dark:bg-gray-700 dark:hover:bg-gray-600 transition-colors duration-200",
    destructive: "bg-red-600 text-white hover:bg-red-500",
    outline:
      "border-2 border-gray-300 hover:bg-gray-200 dark:border-gray-700 dark:hover:bg-gray-700 hover:text-black",
    secondary: "bg-gray-500 text-white hover:bg-gray-400",
    ghost: "hover:bg-gray-100 hover:text-black",
    link: "text-blue-600 underline-offset-4 hover:underline",
  },
  size: {
    default: "h-10 px-4 py-2",
    xs: "h-6 w-6 rounded-md",
    sm: "h-9 rounded-md px-3",
    lg: "h-11 rounded-md px-8",
    long: "h-11 rounded-md px-16",
    icon: "size-10",
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
};

type ButtonVariant = keyof typeof buttonVariants.variant;
type ButtonSize = keyof typeof buttonVariants.size;

interface ButtonProps extends JSX.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  class?: string;
  children?: JSX.Element;
}

const Button = (props: ButtonProps) => {
  const [local, others] = splitProps(props, [
    "variant",
    "size",
    "class",
    "children",
  ]);

  const computedVariant = createMemo(() =>
    (local.variant || buttonVariants.defaultVariants.variant) as ButtonVariant
  );

  const computedSize = createMemo(() =>
    (local.size || buttonVariants.defaultVariants.size) as ButtonSize
  );

  return (
    <button
      class={cn(
        buttonVariants.base,
        buttonVariants.variant[computedVariant()],
        buttonVariants.size[computedSize()],
        local.class,
      )}
      {...others}
    >
      {props?.children}
    </button>
  );
};

export { Button, buttonVariants };
